import { ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { PeriodMode } from '../../../enums/periodMode'
import { EXPERT_PROFIT_AND_LOSS_COLUMN_WIDTHS } from '../constants/expertProfitAndLossColumnWidths'
import { ProfitAndLossAmount } from '../elements/ExpertProfitAndLossTable/elements/ProfitAndLossAmount'
import { ProfitAndLossLabel } from '../elements/ExpertProfitAndLossTable/elements/ProfitAndLossLabel'
import { ProfitAndLossPercentage } from '../elements/ExpertProfitAndLossTable/elements/ProfitAndLossPercentage'
import type { ExpertProfitAndLossPeriodColumn, ExpertProfitAndLossRow } from '../types/expertProfitAndLossView'

export type ExpertProfitAndLossColumnAlign = 'end' | 'start'

export interface ExpertProfitAndLossColumnDef {
  align?: ExpertProfitAndLossColumnAlign
  Cell: (row: ExpertProfitAndLossRow) => ReactNode
  header: ReactNode
  highlighted?: boolean
  id: string
  width?: number | string
}

export const useExpertProfitAndLossColumns = (
  periods: ExpertProfitAndLossPeriodColumn[],
  periodType: PeriodMode | undefined,
  searchTerm = '',
): ExpertProfitAndLossColumnDef[] => {
  const { t } = useTranslation()

  return useMemo((): ExpertProfitAndLossColumnDef[] => {
    const currentPeriod = periods[0]
    const previousPeriod = periods[1]
    const isFiscalYear = periodType === PeriodMode.FiscalYear
    const currentPeriodHeader = isFiscalYear
      ? t('expert_profit_and_loss.table.column.fiscal_year_current')
      : (currentPeriod?.label ?? t('expert_profit_and_loss.table.column.period_current'))
    const previousPeriodHeader = isFiscalYear
      ? t('expert_profit_and_loss.table.column.fiscal_year_previous')
      : (previousPeriod?.label ?? t('expert_profit_and_loss.table.column.period_previous'))

    return [
      {
        Cell: (row) => (
          <ProfitAndLossLabel kind={row.kind} label={row.label} level={row.level} searchTerm={searchTerm} />
        ),
        header: t('expert_profit_and_loss.table.column.item'),
        id: 'item',
        width: EXPERT_PROFIT_AND_LOSS_COLUMN_WIDTHS.item,
      },
      {
        align: 'end',
        Cell: (row) =>
          row.kind === 'heading' ? null : (
            <ProfitAndLossAmount bold={row.kind === 'result' || row.kind === 'subtotal'} value={row.totals[0]} />
          ),
        header: currentPeriodHeader,
        highlighted: true,
        id: 'currentPeriod',
        width: EXPERT_PROFIT_AND_LOSS_COLUMN_WIDTHS.amount,
      },
      {
        align: 'end',
        Cell: (row) =>
          row.kind === 'heading' ? null : (
            <ProfitAndLossAmount bold={row.kind === 'result' || row.kind === 'subtotal'} value={row.totals[1]} />
          ),
        header: previousPeriodHeader,
        id: 'previousPeriod',
        width: EXPERT_PROFIT_AND_LOSS_COLUMN_WIDTHS.amount,
      },
      {
        align: 'end',
        Cell: (row) =>
          row.kind === 'heading' ? null : (
            <ProfitAndLossAmount bold={row.kind === 'result' || row.kind === 'subtotal'} value={row.variance} />
          ),
        header: t('expert_profit_and_loss.table.column.variance_amount'),
        id: 'varianceAmount',
        width: EXPERT_PROFIT_AND_LOSS_COLUMN_WIDTHS.amount,
      },
      {
        align: 'end',
        Cell: (row) => (row.kind === 'heading' ? null : <ProfitAndLossPercentage value={row.variancePercentage} />),
        header: t('expert_profit_and_loss.table.column.variance_percentage'),
        id: 'variancePercentage',
        width: EXPERT_PROFIT_AND_LOSS_COLUMN_WIDTHS.amount,
      },
    ]
  }, [periods, periodType, searchTerm, t])
}
